import type { FormEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '../../../components/EmptyState';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Input,
  PaginationFooter,
  Select,
  Skeleton,
  Textarea,
  useActionDialog,
  type DataTableColumn,
} from '../../../components/ui';
import { useHasPermission } from '../../auth/usePermission';
import { usePointsRewards } from '../../points/hooks/usePointsRewards';
import {
  useGrantPoints,
  usePatientPoints,
  usePatientPointsEntries,
  useRedeemPoints,
  useReversePointsEntry,
} from '../hooks/usePatientPoints';
import type { PointsEntry, PointsReward } from '../../../types/admin';
import { parsePositiveSafeInteger } from '../../../utils/numbers';
import styles from './PatientPointsPanel.module.scss';

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

/** Sentinel for the "not from the catalog" option in the redeem select. */
const FREE_FORM = '__free_form__';

type Props = {
  readonly patientId: string;
};

export function PatientPointsPanel({ patientId }: Props) {
  const { t, i18n } = useTranslation();
  const { confirmAction } = useActionDialog();
  const canManage = useHasPermission('points:manage');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);

  const summaryQuery = usePatientPoints(patientId);
  const entriesQuery = usePatientPointsEntries(patientId, page, pageSize);
  const rewardsQuery = usePointsRewards();

  const grantMutation = useGrantPoints(patientId);
  const redeemMutation = useRedeemPoints(patientId);
  const reverseMutation = useReversePointsEntry(patientId);

  const [grantAmount, setGrantAmount] = useState('');
  const [grantNote, setGrantNote] = useState('');
  const [rewardId, setRewardId] = useState<string>(FREE_FORM);
  const [redeemAmount, setRedeemAmount] = useState('');
  const [redeemNote, setRedeemNote] = useState('');

  const balance = summaryQuery.data?.balance_points ?? 0;
  const rewards = rewardsQuery.data ?? [];
  const selectedReward = rewards.find((r) => r.id === rewardId) ?? null;

  function rewardName(reward: PointsReward): string {
    return i18n.language === 'en' ? reward.name_en : reward.name_uk;
  }

  function entryRewardName(entry: PointsEntry): string | null {
    return (i18n.language === 'en' ? entry.reward_name_en : entry.reward_name_uk) ?? null;
  }

  // The cost actually charged: the catalog price unless the admin typed an override.
  const redeemCost = (() => {
    const typed = parsePositiveSafeInteger(redeemAmount);
    if (typed !== null) return typed;
    return selectedReward?.point_cost ?? 0;
  })();
  const redeemExceedsBalance = redeemCost > balance;

  const busy = grantMutation.isPending || redeemMutation.isPending || reverseMutation.isPending;

  async function handleGrant(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const points = parsePositiveSafeInteger(grantAmount);
    if (points === null) return;
    try {
      await grantMutation.mutateAsync({ points, note: grantNote.trim() || null });
      setGrantAmount('');
      setGrantNote('');
      setPage(1);
    } catch {
      return;
    }
  }

  async function handleRedeem(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (redeemCost <= 0 || redeemExceedsBalance) return;
    const typed = parsePositiveSafeInteger(redeemAmount);
    try {
      await redeemMutation.mutateAsync({
        reward_id: rewardId === FREE_FORM ? null : rewardId,
        points: typed,
        note: redeemNote.trim() || null,
      });
      setRewardId(FREE_FORM);
      setRedeemAmount('');
      setRedeemNote('');
      setPage(1);
    } catch {
      return;
    }
  }

  async function handleReverse(entry: PointsEntry) {
    const confirmed = await confirmAction({
      message: t('points.confirmReverse'),
      confirmLabel: t('points.reverseCta'),
      tone: 'warning',
    });
    if (!confirmed) return;
    reverseMutation.mutate({ entryId: entry.id });
  }

  const columns: readonly DataTableColumn<PointsEntry>[] = [
    {
      key: 'created_at',
      header: t('points.colDate'),
      render: (row) => new Date(row.created_at).toLocaleString(i18n.language),
    },
    {
      key: 'delta',
      header: t('points.colDelta'),
      align: 'right',
      render: (row) => (
        <span className={row.delta_points > 0 ? styles.deltaPositive : styles.deltaNegative}>
          {row.delta_points > 0 ? `+${row.delta_points}` : row.delta_points}
        </span>
      ),
    },
    {
      key: 'reason',
      header: t('points.colReason'),
      render: (row) => (
        <span className={styles.reasonCell}>
          {t(`points.reasonLabels.${row.reason}`, { defaultValue: row.reason })}
          {row.reversed_by_entry_id ? (
            <Badge variant="neutral" size="sm">
              {t('points.reversedBadge')}
            </Badge>
          ) : null}
        </span>
      ),
    },
    {
      key: 'reward',
      header: t('points.colReward'),
      hideBelow: 'md',
      render: (row) => entryRewardName(row) ?? '—',
    },
    {
      key: 'note',
      header: t('points.colNote'),
      hideBelow: 'md',
      render: (row) => row.note ?? '—',
    },
    {
      key: 'admin',
      header: t('points.colAdmin'),
      hideBelow: 'md',
      render: (row) => row.created_by_admin_name ?? '—',
    },
    ...(canManage
      ? [
          {
            key: 'actions',
            header: '',
            align: 'right' as const,
            render: (row: PointsEntry) =>
              row.reason === 'reversal' || row.reversed_by_entry_id ? null : (
                <Button variant="danger" size="sm" onClick={() => handleReverse(row)} disabled={busy}>
                  {t('points.reverseCta')}
                </Button>
              ),
          },
        ]
      : []),
  ];

  if (summaryQuery.isLoading) {
    return (
      <div className={styles.panel}>
        <Skeleton height={96} />
        <Skeleton height={220} />
      </div>
    );
  }

  if (summaryQuery.isError) {
    return (
      <EmptyState
        title={t('points.loadErrorTitle')}
        description={
          summaryQuery.error instanceof Error
            ? summaryQuery.error.message
            : t('points.loadErrorDescription')
        }
      />
    );
  }

  const summary = summaryQuery.data;
  const entries = entriesQuery.data;

  return (
    <div className={styles.panel}>
      <Card padding="md">
        <div className={styles.balanceRow}>
          <div className={styles.balanceMain}>
            <span className={styles.balanceLabel}>{t('points.balance')}</span>
            <span className={styles.balanceValue}>
              {summary?.balance_points ?? 0}
              <span className={styles.balanceUnit}>{t('points.balanceUnit')}</span>
            </span>
            <span className={styles.balanceUah}>
              {t('points.balanceUah', {
                amount: summary?.balance_uah ?? '0.00',
                currency: summary?.currency ?? 'UAH',
              })}
            </span>
          </div>
          <div className={styles.lifetime}>
            <span className={styles.lifetimeItem}>
              <span className={styles.lifetimeLabel}>{t('points.lifetimeGranted')}</span>
              {summary?.lifetime_granted_points ?? 0}
            </span>
            <span className={styles.lifetimeItem}>
              <span className={styles.lifetimeLabel}>{t('points.lifetimeRedeemed')}</span>
              {summary?.lifetime_redeemed_points ?? 0}
            </span>
          </div>
        </div>
        <p className={styles.pricingHint}>{t('points.pricingHint')}</p>
      </Card>

      {canManage ? (
        <div className={styles.forms}>
          <Card padding="none">
            <CardHeader title={t('points.grantTitle')} />
            <form onSubmit={handleGrant}>
              <CardBody>
                <div className={styles.formGrid}>
                  <Input
                    id="points-grant-amount"
                    type="number"
                    min={1}
                    label={t('points.grantPoints')}
                    value={grantAmount}
                    onChange={(e) => setGrantAmount(e.target.value)}
                  />
                  <Textarea
                    id="points-grant-note"
                    wrapperClassName={styles.fieldFull}
                    label={t('points.grantNote')}
                    placeholder={t('points.grantNotePlaceholder')}
                    value={grantNote}
                    onChange={(e) => setGrantNote(e.target.value)}
                    rows={2}
                  />
                </div>
                {grantMutation.error instanceof Error ? (
                  <p className={styles.inlineError}>{grantMutation.error.message}</p>
                ) : null}
                <div className={styles.formActions}>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={busy || parsePositiveSafeInteger(grantAmount) === null}
                  >
                    {grantMutation.isPending ? t('points.granting') : t('points.grantCta')}
                  </Button>
                </div>
              </CardBody>
            </form>
          </Card>

          <Card padding="none">
            <CardHeader title={t('points.redeemTitle')} />
            <form onSubmit={handleRedeem}>
              <CardBody>
                <div className={styles.formGrid}>
                  <Select
                    id="points-redeem-reward"
                    label={t('points.redeemReward')}
                    value={rewardId}
                    onChange={(e) => {
                      setRewardId(e.target.value);
                      setRedeemAmount('');
                    }}
                  >
                    <option value={FREE_FORM}>{t('points.redeemFreeForm')}</option>
                    {rewards.map((reward) => (
                      <option key={reward.id} value={reward.id} disabled={reward.point_cost > balance}>
                        {`${rewardName(reward)} — ${reward.point_cost}`}
                      </option>
                    ))}
                  </Select>
                  <Input
                    id="points-redeem-amount"
                    type="number"
                    min={1}
                    label={t('points.redeemPoints')}
                    hint={selectedReward ? t('points.redeemPointsOverrideHint') : undefined}
                    placeholder={selectedReward ? String(selectedReward.point_cost) : ''}
                    value={redeemAmount}
                    onChange={(e) => setRedeemAmount(e.target.value)}
                  />
                  <Textarea
                    id="points-redeem-note"
                    wrapperClassName={styles.fieldFull}
                    label={t('points.redeemNote')}
                    value={redeemNote}
                    onChange={(e) => setRedeemNote(e.target.value)}
                    rows={2}
                  />
                </div>
                {redeemExceedsBalance && redeemCost > 0 ? (
                  <p className={styles.inlineError}>{t('points.redeemInsufficient')}</p>
                ) : null}
                {redeemMutation.error instanceof Error ? (
                  <p className={styles.inlineError}>{redeemMutation.error.message}</p>
                ) : null}
                <div className={styles.formActions}>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={busy || redeemCost <= 0 || redeemExceedsBalance}
                  >
                    {redeemMutation.isPending ? t('points.redeeming') : t('points.redeemCta')}
                  </Button>
                </div>
              </CardBody>
            </form>
          </Card>
        </div>
      ) : null}

      {reverseMutation.error instanceof Error ? (
        <p className={styles.inlineError}>{reverseMutation.error.message}</p>
      ) : null}

      <Card padding="none">
        <CardHeader title={t('points.historyTitle')} />
        <DataTable<PointsEntry>
          columns={columns}
          rows={entries?.items ?? []}
          rowKey={(row) => row.id}
          isLoading={entriesQuery.isLoading}
          emptyMessage={t('points.emptyDescription')}
          footer={
            entries && entries.total > 0 ? (
              <PaginationFooter
                page={entries.page}
                pageSize={entries.page_size}
                total={entries.total}
                pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
              />
            ) : null
          }
        />
      </Card>
    </div>
  );
}
